export const EMPTY_FRAME = Object.freeze({ columns: Object.freeze([]), rows: Object.freeze([]) });

export function isMissing(value) {
  return value === null || value === undefined || (typeof value === "number" && Number.isNaN(value));
}

export function frameFromRows(rows) {
  const seen = new Set();
  const columns = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }
  return { columns, rows };
}

export function isEmptyFrame(frame) {
  return !frame || frame.rows.length === 0;
}

export function hasColumn(frame, column) {
  return !!frame && frame.columns.includes(column);
}

export function concatFrames(frames) {
  const nonEmpty = frames.filter((f) => !isEmptyFrame(f));
  if (nonEmpty.length === 0) return EMPTY_FRAME;
  if (nonEmpty.length === 1) return nonEmpty[0];
  const seen = new Set();
  const columns = [];
  const rows = [];
  for (const frame of nonEmpty) {
    for (const column of frame.columns) {
      if (!seen.has(column)) {
        seen.add(column);
        columns.push(column);
      }
    }
    for (const row of frame.rows) rows.push(row);
  }
  return { columns, rows };
}

export function filterRows(frame, predicate) {
  return { columns: frame.columns, rows: frame.rows.filter(predicate) };
}

function keyOf(row, keys) {
  return JSON.stringify(keys.map((k) => (isMissing(row[k]) ? null : row[k])));
}

export function dropDuplicates(frame, keys) {
  const seen = new Set();
  const rows = [];
  for (const row of frame.rows) {
    const key = keyOf(row, keys);
    if (!seen.has(key)) {
      seen.add(key);
      rows.push(row);
    }
  }
  return { columns: frame.columns, rows };
}

export function leftMergeUnique(left, right, keys, suffix) {
  const leftColumns = new Set(left.columns);
  const rename = {};
  const addedColumns = [];
  for (const column of right.columns) {
    if (keys.includes(column)) continue;
    const target = leftColumns.has(column) ? `${column}${suffix}` : column;
    rename[column] = target;
    addedColumns.push(target);
  }

  const index = new Map();
  for (const row of right.rows) {
    const key = keyOf(row, keys);
    if (!index.has(key)) index.set(key, row);
  }

  const rows = left.rows.map((row) => {
    const match = index.get(keyOf(row, keys));
    const merged = { ...row };
    for (const [from, to] of Object.entries(rename)) {
      merged[to] = match ? match[from] : undefined;
    }
    return merged;
  });

  return { columns: [...left.columns, ...addedColumns], rows };
}

export function isNumericColumn(frame, column) {
  if (!hasColumn(frame, column)) return false;
  for (const row of frame.rows) {
    const value = row[column];
    if (isMissing(value)) continue;
    if (typeof value !== "number" && typeof value !== "boolean") return false;
  }
  return true;
}

export function columnValues(frame, column) {
  const out = [];
  for (const row of frame.rows) {
    const value = row[column];
    if (!isMissing(value)) out.push(value);
  }
  return out;
}

export function uniqueValues(frame, column) {
  return [...new Set(columnValues(frame, column))];
}
